// The DESIGNER CATALOG — what the shared column editors (designerKit) ask the
// host for, instead of importing a host's HTTP client, toast or dialogs.
//
// The transaction designer and the additional-attributes setup screen edit the
// SAME ColumnDef properties with the SAME editors (lookup, item change,
// validation, edit mask, display format). The editors live here, in the shared
// package, so the platform builder, the platform Run view and the downloaded /
// J2EE runtime mount one implementation. What differs per host is only where
// tables and columns come from and how a message or a confirmation is shown:
//
//   * platform designer — frontend/src/pages/designers/transaction/designerCatalog.ts
//     (the Database Designer catalog + the deployed preview schema, toasts,
//     the platform Modal and ConfirmDialog);
//   * setup screen      — uda/designer/catalog.ts over the UdaTransport's
//     read-only `catalog/tables` + `catalog/columns` (the tenant data DB).
//
// Every call may reject; the editors degrade to free text and say so.

import type { ComponentType, ReactNode } from 'react'

export interface KitTable { name: string; description?: string }
export interface KitColumn { column_name: string; data_type?: string }
export interface KitTransaction { obj_name: string; title: string; main_table: string }
export interface KitProjectVariable { name: string; label?: string }

export interface KitConfirmOptions {
  title?: string
  message: ReactNode
  confirmLabel?: string
  cancelLabel?: string
  /** 'danger' (default) paints the confirm button red; 'primary' is neutral. */
  tone?: 'danger' | 'primary'
}

export interface KitModalProps {
  open: boolean
  onClose: () => void
  title?: string
  children: ReactNode
  footer?: ReactNode
  size?: 'default' | 'wide' | 'full'
}

export type KitNotifyKind = 'error' | 'success' | 'info'

export interface DesignerCatalog {
  /** Tables a lookup / rule / validation may name. */
  tables(): Promise<KitTable[]>
  /** Columns of one table. A table the catalog does not know rejects with a
   *  404-shaped error (see `isNotFound`) or resolves `[]`. */
  columns(table: string): Promise<KitColumn[]>
  /** Optional second source merged into the lookup editor's pickers (the
   *  platform's deployed preview schema next to the designed tables). */
  liveTables?(): Promise<KitTable[]>
  liveColumns?(table: string): Promise<KitColumn[]>
  /** Transaction objects (the lookup picker's "+ Add" target). */
  transactions?(): Promise<KitTransaction[]>
  /** Project variables, offered as {var.NAME} tokens. */
  projectVariables?(): Promise<KitProjectVariable[]>
  /** Active inbound IOFlow API names (a validation's Call API picker). */
  integrationApis?(): Promise<string[]>
  /** Tell the person something (replaces a host toast). */
  notify(kind: KitNotifyKind, message: string): void
  /** Ask a yes/no question. Absent ⇒ the kit's own dialog (useKitConfirm). */
  confirm?(opts: KitConfirmOptions): Promise<boolean>
  /** The host's modal shell. Absent ⇒ the kit's own (KitModal). */
  Modal?: ComponentType<KitModalProps>
}

/** A catalog that knows nothing: every picker degrades to free text. */
export const EMPTY_CATALOG: DesignerCatalog = {
  tables: async () => [],
  columns: async () => [],
  notify: (kind, message) => {
    // No host surface to show it on — the console keeps it findable.
    if (kind === 'error') console.error(`[designerKit] ${message}`)
    else console.info(`[designerKit] ${message}`)
  },
}

/** Whether a rejected catalog call means "no such table" (not a failure). */
export function isNotFound(err: unknown): boolean {
  if (!err || typeof err !== 'object') return false
  const e = err as { status?: unknown; response?: { status?: unknown } }
  return e.status === 404 || e.response?.status === 404
}

/** A human message for a rejected call. */
export function errorMessage(err: unknown, fallback: string): string {
  if (!err) return fallback
  if (typeof err === 'string') return err
  const e = err as { message?: unknown; response?: { data?: { detail?: unknown } } }
  const detail = e.response?.data?.detail
  if (typeof detail === 'string' && detail) return detail
  if (detail && typeof detail === 'object' && typeof (detail as { message?: unknown }).message === 'string') {
    return String((detail as { message: string }).message)
  }
  return typeof e.message === 'string' && e.message ? e.message : fallback
}

export function cmpNoCase(a: string, b: string): number {
  return a.toLowerCase().localeCompare(b.toLowerCase())
}

/** Union of table lists by case-insensitive name (first description wins). */
export function mergeTables(...lists: Array<KitTable[] | null | undefined>): KitTable[] {
  const byKey = new Map<string, KitTable>()
  for (const list of lists) {
    for (const t of list || []) {
      const n = String(t?.name || '').trim()
      if (!n) continue
      const key = n.toLowerCase()
      const had = byKey.get(key)
      if (!had) byKey.set(key, { name: n, description: t.description })
      else if (t.description && !had.description) byKey.set(key, { name: had.name, description: t.description })
    }
  }
  return [...byKey.values()].sort((a, b) => cmpNoCase(a.name, b.name))
}

/** Union of column lists by case-insensitive name (first type wins). */
export function mergeColumns(...lists: Array<KitColumn[] | null | undefined>): KitColumn[] {
  const byKey = new Map<string, KitColumn>()
  for (const list of lists) {
    for (const c of list || []) {
      const n = String(c?.column_name || '').trim()
      if (!n) continue
      const key = n.toLowerCase()
      const had = byKey.get(key)
      if (!had) byKey.set(key, { column_name: n, data_type: c.data_type })
      else if (c.data_type && !had.data_type) byKey.set(key, { column_name: had.column_name, data_type: c.data_type })
    }
  }
  return [...byKey.values()]
}
